import React from 'react'
import PropTypes from 'prop-types'
import { useUniqueId } from '@ds/react-utils'
import { useDsUiDataProps } from '../../theming'
import { SelectionControlGroup } from '../../internal/components/SelectionControlGroup'
import type { ConditionalReactElementChildOrArray } from '../../types'
import { RadioButton, RadioButtonProps } from '../RadioButton'

export interface RadioButtonGroupProps {
  /**
   * The 'children' prop accepts arbitrary nodes, but the normal usage pattern is:
   * to pass RadioButton components as children
   */
  children: ConditionalReactElementChildOrArray<RadioButtonProps>
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Applies a 'description' treatment, displaying the provided text.
   */
  description?: string
  /**
   * Disables all child RadioButtonGroups and RadioButtons. Also, adds a disabled
   * style to the legend element
   */
  disabled?: boolean
  /**
   * Applies an 'error' treatment, displaying the provided text.
   */
  error?: string
  /**
   * The identifier used to group a set of RadioButtons.
   */
  groupIdentifier?: string
  /**
   * Hide the RadioButtonGroup legend while allowing assistive devices to identify the control.
   */
  hideLegend?: boolean
  /**
   * The legend for the radio button group.
   */
  legend: string
  /**
   * Adds a visual treatment to the legend indicating it is required.
   */
  required?: boolean
}

/**
 * Radio Button Groups let users choose a single selection among a group of mutually exclusive choices.
 */
export function RadioButtonGroup({
  children,
  'data-qa': dataQa,
  description,
  disabled = false,
  error,
  groupIdentifier,
  hideLegend = false,
  legend,
  required = false,
  ...restProps
}: RadioButtonGroupProps) {
  const groupIdentifierId = useUniqueId('short')

  const dsUiDataProps = useDsUiDataProps(RadioButtonGroup)

  const renderChildren = () =>
    React.Children.map(children, (child, index) => {
      if (!React.isValidElement(child)) return null
      if (child.type === RadioButtonGroup) {
        return (
          <div
            className="selectionControlGroupNestedWrap"
            data-qa={dataQa && `${dataQa}-nested-wrap-${index}`}
          >
            {React.cloneElement(child, {
              ...(disabled && { disabled }),
            })}
          </div>
        )
      }

      const itemBottomMarginClassName =
        child.props.description &&
        Array.isArray(children) &&
        children.length - 1 !== index
          ? 'selectionControlGroupItemBottomMargin'
          : ''

      return (
        <div
          data-qa={dataQa && `${dataQa}-control-group-${index}`}
          className={`selectionControlGroupItem ${itemBottomMarginClassName}`}
        >
          {child.type === RadioButton
            ? React.cloneElement(child, {
                ...(disabled && { disabled }),
                groupIdentifier: groupIdentifier || groupIdentifierId,
                invalid: !!error,
                'aria-invalid': error ? undefined : undefined,
              })
            : child}
        </div>
      )
    })

  return (
    <SelectionControlGroup
      {...dsUiDataProps}
      {...restProps}
      data-qa={dataQa}
      description={description}
      disabled={disabled}
      error={error}
      hideLegend={hideLegend}
      legend={legend}
      renderChildren={renderChildren}
      required={required}
      role="radiogroup"
    />
  )
}

RadioButtonGroup.propTypes = {
  children: PropTypes.node.isRequired,
  'data-.*': PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  groupIdentifier: PropTypes.string,
  hideLegend: PropTypes.bool,
  legend: PropTypes.string.isRequired,
  required: PropTypes.bool,
}

RadioButtonGroup.displayName = 'RadioButtonGroup'
